<template>
    <div class="other-courses">
        <button type="button" class="accordion-trigger" @click="isExpanded = !isExpanded">
            <span>
                <strong>{{ $t('otherCourses') }}</strong>
                <small>{{ $t('coursesFromOtherTerms') }} · {{ selectedSummary }}</small>
            </span>
            <svg
                :class="{ open: isExpanded }"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
            >
                <polyline points="6 9 12 15 18 9" />
            </svg>
        </button>

        <n-collapse-transition :show="isExpanded">
            <div class="accordion-body">
                <div v-for="group in courseGroups" :key="group.semester" class="semester-group">
                    <h3>{{ group.semester }}. {{ $t('semester') }}</h3>
                    <div class="course-table">
                        <label
                            v-for="course in group.courses"
                            :key="course.id"
                            class="course-row"
                            :class="{
                                selected: isSelected(course.id),
                                disabled: isDisabled(course.id)
                            }"
                        >
                            <input
                                type="checkbox"
                                :checked="isSelected(course.id)"
                                :disabled="isDisabled(course.id)"
                                @change="toggleCourse(course, $event.target.checked)"
                            >
                            <span class="course-name">{{ course.name }}</span>
                            <span class="course-mode">{{ $t('offline') }}</span>
                            <span class="course-price">{{ course.price }} €</span>
                        </label>
                    </div>
                </div>
            </div>
        </n-collapse-transition>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { NCollapseTransition } from 'naive-ui'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
    allCourses: { type: Array, required: true },
    courseList: { type: Array, required: true },
    maxCourses: { type: Number, required: true },
    otherSelectedCount: { type: Number, default: 0 }
})

const emit = defineEmits(['update:courseList'])
const selectedCourses = ref([])
const isExpanded = ref(false)

function mapCourse(course) {
    return {
        id: course.id,
        name: course.name,
        price: String(course.price ?? ''),
        semester: course.semester,
        is_online: false,
        selected: true
    }
}

function syncFromProps() {
    selectedCourses.value = props.courseList.map(mapCourse)
}

syncFromProps()
watch(() => props.courseList, syncFromProps, { deep: true })

const selectedIds = computed(() => selectedCourses.value.map(course => course.id))
const totalSelected = computed(() => selectedCourses.value.length + props.otherSelectedCount)
const selectedSummary = computed(() => selectedCourses.value.length
    ? t('coursesSelected', { count: selectedCourses.value.length })
    : t('noCoursesSelected'))

const courseGroups = computed(() => {
    const groups = new Map()

    props.allCourses.forEach(course => {
        const semester = course.semester || '—'
        if (!groups.has(semester)) groups.set(semester, [])
        groups.get(semester).push(course)
    })

    return Array.from(groups.entries())
        .sort(([left], [right]) => Number(left) - Number(right))
        .map(([semester, courses]) => ({ semester, courses }))
})

function isSelected(courseId) {
    return selectedIds.value.includes(courseId)
}

function isDisabled(courseId) {
    return !isSelected(courseId) && totalSelected.value >= props.maxCourses
}

function toggleCourse(course, checked) {
    if (checked) {
        if (totalSelected.value >= props.maxCourses || isSelected(course.id)) return
        selectedCourses.value.push(mapCourse(course))
    } else {
        selectedCourses.value = selectedCourses.value.filter(item => item.id !== course.id)
    }

    const selectedSet = new Set(selectedCourses.value.map(item => item.id))
    selectedCourses.value = props.allCourses
        .filter(item => selectedSet.has(item.id))
        .map(mapCourse)

    emit('update:courseList', selectedCourses.value)
}
</script>

<style scoped>
.accordion-trigger {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--text);
    cursor: pointer;
    text-align: left;
}

.accordion-trigger strong {
    display: block;
    font: 600 20px var(--font-heading);
}

.accordion-trigger small {
    display: block;
    margin-top: 4px;
    color: var(--muted);
    font-size: 13px;
}

.accordion-trigger svg {
    flex: none;
    transition: transform .2s ease;
}

.accordion-trigger svg.open {
    transform: rotate(180deg);
}

.accordion-body {
    display: grid;
    gap: 22px;
    padding-top: 24px;
}

.semester-group h3 {
    margin: 0 0 8px;
    color: var(--muted);
    font: 600 12px var(--font-body);
    letter-spacing: .06em;
    text-transform: uppercase;
}

.course-table {
    overflow: hidden;
    border: 1px solid var(--border);
    border-radius: 12px;
}

.course-row {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) 90px 100px;
    gap: 12px;
    align-items: center;
    min-height: 52px;
    padding: 12px 16px;
    border-top: 1px solid #f0ebdc;
    background: var(--surface);
    cursor: pointer;
    transition: background-color .15s ease, opacity .15s ease;
}

.course-row:first-child {
    border-top: 0;
}

.course-row.selected {
    background: var(--accent-tint);
}

.course-row.disabled {
    opacity: .45;
    cursor: not-allowed;
}

.course-row input {
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: var(--accent);
    cursor: inherit;
}

.course-name {
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.45;
}

.course-price {
    text-align: right;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
}

.course-mode {
    justify-self: start;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--soft);
    color: #555;
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
}

@media (max-width: 640px) {
    .course-row {
        grid-template-columns: 28px minmax(0, 1fr) 68px 76px;
        gap: 8px;
        padding: 11px 12px;
    }
}
</style>
